import { useSyncExternalStore } from "react";

// In-progress listings. A seller snaps a photo, the AI analysis runs in the
// background, and the listing stays a local draft — shown as a minimized bar —
// until they actually submit it. Several can be in flight at once, so a maker
// can photograph a whole table of product and fill the details in afterward.
//
// Everything is persisted to IndexedDB (photo included), so a draft survives
// navigation, a reload, and an app kill. Nothing is written to the server until
// the seller submits: an unfinished listing is theirs, not a half-published row
// in their Drafts tab.

export type ComposerAnalysis = {
  name: string;
  category: string;
  subtype: string | null;
  description: string;
  estimatedWeightLabel: string | null;
  suggestedPriceMin: number | null;
  suggestedPriceMax: number | null;
  unit: string;
  growthState: "ripe" | "growing";
  daysUntilRipe: number | null;
  isWholeAnimal?: boolean;
  kind?: string;
};

export type ComposerStatus = "analyzing" | "queued_offline" | "ready" | "error";

export type ComposerDraft = {
  id: string;
  imageBase64: string;
  allImageBase64?: string[];
  status: ComposerStatus;
  analysis?: ComposerAnalysis;
  error?: string;
  createdAt: number;
};

const DB_NAME = "stoop-listing-composer";
const STORE = "draft";
const KEY = "drafts";

let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function persist(list: ComposerDraft[]): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const store = db.transaction(STORE, "readwrite").objectStore(STORE);
    const req = list.length ? store.put(list, KEY) : store.delete(KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function load(): Promise<ComposerDraft[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = db.transaction(STORE, "readonly").objectStore(STORE);
    const req = store.get(KEY);
    req.onsuccess = () => resolve((req.result as ComposerDraft[]) ?? []);
    req.onerror = () => reject(req.error);
  });
}

function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const buf = new Uint8Array(8);
  c?.getRandomValues?.(buf);
  return `${Date.now().toString(16)}-${Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

let drafts: ComposerDraft[] = [];
const listeners = new Set<() => void>();
function emit() {
  for (const l of listeners) l();
}
function commit(next: ComposerDraft[]) {
  drafts = next;
  void persist(next);
  emit();
}
function patch(id: string, p: Partial<ComposerDraft>) {
  const found = drafts.find((d) => d.id === id);
  if (!found) return;
  commit(drafts.map((d) => (d.id === id ? { ...d, ...p } : d)));
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

async function analyze(imageBase64: string): Promise<ComposerAnalysis> {
  const res = await fetch("/api/ai/analyze-product-image", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ imageBase64, mediaType: "image/jpeg" }),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => `Analyze failed (${res.status})`));
  return (await res.json()) as ComposerAnalysis;
}

const running = new Set<string>();
async function runAnalysis(id: string): Promise<void> {
  if (running.has(id)) return;
  const draft = drafts.find((d) => d.id === id);
  if (!draft) return;
  if (isOffline()) {
    patch(id, { status: "queued_offline" });
    return;
  }
  running.add(id);
  patch(id, { status: "analyzing", error: undefined });
  try {
    const analysis = await analyze(draft.imageBase64);
    // The draft may have been published or discarded while we awaited.
    if (!drafts.some((d) => d.id === id)) return;
    patch(id, { status: "ready", analysis });
  } catch (err) {
    if (!drafts.some((d) => d.id === id)) return;
    patch(id, {
      status: isOffline() ? "queued_offline" : "error",
      error: String((err as Error)?.message || err),
    });
  } finally {
    running.delete(id);
  }
}

// Public API ---------------------------------------------------------------

// Start a new in-progress listing from a captured photo. Returns its id so the
// dialog can keep showing that specific draft.
export function beginComposer(imageBase64: string, allImageBase64?: string[]): string {
  const id = newId();
  commit([
    ...drafts,
    { id, imageBase64, allImageBase64, status: "analyzing", createdAt: Date.now() },
  ]);
  void runAnalysis(id);
  return id;
}

export function getDraft(id: string | null | undefined): ComposerDraft | null {
  if (!id) return null;
  return drafts.find((d) => d.id === id) ?? null;
}

// Called on publish or discard. Until this runs the draft stays in the tray,
// which is the point: nothing reaches the server unfinished.
export function removeDraft(id: string): void {
  commit(drafts.filter((d) => d.id !== id));
}

export function retryDraft(id: string): void {
  void runAnalysis(id);
}

// The tray asks the seller dashboard to reopen a specific draft. A flag rather
// than an event so it survives the navigation to /seller before the dashboard
// has mounted to hear it.
let openRequestId: string | null = null;
export function requestOpenDraft(id: string): void {
  openRequestId = id;
  emit();
}
export function consumeOpenRequest(): string | null {
  const id = openRequestId;
  openRequestId = null;
  // Emit so the subscribed snapshot goes back to null. Without this the store
  // still reports the consumed id, and tapping the SAME draft again wouldn't
  // change the snapshot — so the reopen effect would never fire a second time.
  if (id) emit();
  return id;
}

// The open request needs its own subscription. Keying the dashboard's effect on
// the drafts array didn't work: requesting an open doesn't mutate that array, so
// useSyncExternalStore returned an identical reference, nothing re-rendered, and
// tapping a tray bar did nothing at all.
export function useOpenRequestId(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => openRequestId,
    () => null,
  );
}

let started = false;
export function initListingComposer(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  void load().then((saved) => {
    if (!saved.length) return;
    drafts = saved;
    emit();
    // An analysis interrupted by a kill/reload resumes; a finished one just
    // waits in the tray for the seller to open it.
    for (const d of drafts) {
      if (d.status === "analyzing" || d.status === "queued_offline") void runAnalysis(d.id);
    }
  });
  window.addEventListener("online", () => {
    for (const d of drafts) {
      if (d.status === "queued_offline" || d.status === "error") void runAnalysis(d.id);
    }
  });
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

const EMPTY: ComposerDraft[] = [];
export function useComposerDrafts(): ComposerDraft[] {
  return useSyncExternalStore(
    subscribe,
    () => drafts,
    () => EMPTY,
  );
}
